/**
 * Kidify email protection
 *
 * Addresses reach the page scrambled (snippets/kidify-email.liquid and
 * snippets/kidify-protect-emails.liquid): a placeholder span carrying the
 * address reversed and base64 encoded, or a link whose href is
 * #kidify-email:<code>. Nothing in the HTML reads as an email address.
 *
 * The real addresses go in on the first trusted interaction: a tap,
 * click, mouse move, wheel or key press. Harvesters that read the HTML
 * never get that far, and most headless ones load the page without
 * generating real input events (isTrusted is false for scripted ones).
 * People do one of these within moments of arriving, and pressing or
 * tabbing to an address reveals it before the click lands, so the link
 * opens their mail app as normal.
 */

const PLACEHOLDER = 'span[data-kidify-email]';
const TOKEN_LINK = 'a[href^="#kidify-email:"]';
// Only events a script can't fake. Scrolling and focus are left out on
// purpose: window.scrollTo() and element.focus() make the browser fire
// scroll and focus events marked as trusted, and bots scroll to load
// lazy images. People scroll by touch, wheel or keys, which are here.
const EVENTS = ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel', 'click'];

const decode = (code) => {
  try {
    return atob(code).split('').reverse().join('');
  } catch {
    return '';
  }
};

function upgradeLink(link) {
  const [code, query = ''] = link.getAttribute('href').slice('#kidify-email:'.length).split('?');
  const address = decode(code);
  if (address) link.setAttribute('href', `mailto:${address}${query ? `?${query}` : ''}`);
}

function upgradePlaceholder(span) {
  const address = decode(span.dataset.kidifyEmail);
  if (!address) return;

  const link = span.closest('a');
  if (link) {
    // Already inside a link (kidify-protect-emails keeps the merchant's
    // own <a>): the text is enough.
    if (link.getAttribute('href')?.startsWith('#kidify-email:')) upgradeLink(link);
    span.replaceWith(document.createTextNode(address));
    return;
  }

  const anchor = document.createElement('a');
  anchor.className = span.className;
  anchor.href = `mailto:${address}`;
  anchor.textContent = address;
  const hadFocus = document.activeElement === span;
  span.replaceWith(anchor);
  if (hadFocus) anchor.focus();
}

function upgrade(root) {
  root.querySelectorAll(TOKEN_LINK).forEach(upgradeLink);
  root.querySelectorAll(PLACEHOLDER).forEach(upgradePlaceholder);
}

let revealed = false;

function reveal() {
  if (revealed) return;
  revealed = true;
  for (const type of EVENTS) window.removeEventListener(type, onInput, true);
  upgrade(document);

  // Sections re-rendered later (filters, the cart, the theme editor) can
  // bring new placeholders; this visitor has already shown they're human.
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches?.(`${PLACEHOLDER}, ${TOKEN_LINK}`)) upgrade(node.parentElement || document);
        else upgrade(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}

function onInput(event) {
  if (event.isTrusted) reveal();
}

for (const type of EVENTS) window.addEventListener(type, onInput, { capture: true, passive: true });
